import type { Tenant } from '../../types/lac';
import {
  BarChart3,
  Building2,
  Coins,
  FileText,
  Globe,
  Users,
} from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksPageHeading } from '../../components/liks/LiksPageHeading/LiksPageHeading';
import { LiksTabs } from '../../components/liks/LiksTabs/LiksTabs';
import { useLacCostLimitQuery } from '../../features/lac/queries/useLacCostLimitQuery';
import { useLacTenantOverviewQuery } from '../../features/lac/queries/useLacTenantOverviewsQuery';
import { useLacTenantQuery } from '../../features/lac/queries/useLacTenantQuery';
import { useI18n } from '../../i18n/useI18n';
import './LacTenantDetailsPage.css';
import { LacDocumentsTab } from './tabs/LacDocumentsTab';
import { LacTenantCostsTab } from './tabs/LacTenantCostsTab';
import { LacTenantMembersTab } from './tabs/LacTenantMembersTab';
import { LacTenantOrganisationTab } from './tabs/LacTenantOrganisationTab';
import { LacTenantOverviewStats } from './tabs/LacTenantOverviewStats';
import { LacWebsiteCrawlsTab } from './tabs/LacWebsiteCrawlsTab';
import { getCurrentMonthDateRange } from '../../features/lac/tenantOverviewUtils';
import {
  DEFAULT_LAC_TENANT_DETAILS_TAB,
  isLacTenantDetailsTabId,
  resolveLacTenantPath,
  type LacTenantDetailsTabId,
} from '../../routes/lacPaths';
import { resolveLacTabPath } from '../../routes/lacPaths';

function tenantDisplayName(tenant: Tenant): string {
  return tenant.displayName?.trim() || tenant.name;
}

export function LacTenantDetailsPage() {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const { tenantName, tab } = useParams<{
    tenantName?: string;
    tab?: string;
  }>();
  const monthRange = getCurrentMonthDateRange();

  const tenantQuery = useLacTenantQuery(tenantName);
  const overviewQuery = useLacTenantOverviewQuery(tenantName, monthRange);
  const costLimitQuery = useLacCostLimitQuery(tenantName);
  const tenant = tenantQuery.data;

  if (!tenantName) {
    return <Navigate to={resolveLacTabPath('organisations')} replace />;
  }

  if (!isLacTenantDetailsTabId(tab)) {
    return (
      <Navigate
        to={resolveLacTenantPath(tenantName, DEFAULT_LAC_TENANT_DETAILS_TAB)}
        replace
      />
    );
  }

  const activeTabId: LacTenantDetailsTabId = tab;

  if (tenantQuery.isLoading) {
    return (
      <LiksPage
        size="big"
        content={
          <p className="lac-tenant-details__status">
            {translate('lac.organisations.details.loading')}
          </p>
        }
      />
    );
  }

  if (tenantQuery.isError || !tenant) {
    return (
      <LiksPage
        size="big"
        header={
          <LiksPageHeading
            aria-label={translate('lac.organisations.details.breadcrumbLegend')}
            items={[
              {
                label: translate('lac.title'),
                to: resolveLacTabPath('organisations'),
              },
              { label: translate('lac.organisations.details.title') },
            ]}
          />
        }
        content={
          <p className="lac-tenant-details__status" role="alert">
            {translate('lac.organisations.details.loadError')}
          </p>
        }
      />
    );
  }

  if (tenant.name !== tenantName) {
    return (
      <Navigate to={resolveLacTenantPath(tenant.name, activeTabId)} replace />
    );
  }

  const headingName = tenantDisplayName(tenant);

  const handleTabChange = (nextTabId: string) => {
    if (!isLacTenantDetailsTabId(nextTabId)) {
      return;
    }
    navigate(resolveLacTenantPath(tenantName, nextTabId));
  };

  const tabs = [
    {
      id: 'organisation',
      label: translate('lac.organisations.tabs.organisation'),
      icon: <Building2 aria-hidden />,
      content: (
        <LacTenantOrganisationTab tenant={tenant} tenantName={tenantName} />
      ),
    },
    {
      id: 'costs',
      label: translate('lac.organisations.tabs.costs'),
      icon: <Coins aria-hidden />,
      content: (
        <LacTenantCostsTab
          key={`${tenantName}:${costLimitQuery.data?.stamp ?? costLimitQuery.data?.monthlyLimit ?? costLimitQuery.data?.yearlyLimit ?? 'none'}`}
          tenantName={tenantName}
          overview={overviewQuery.data}
          monthlyCostLimit={costLimitQuery.data?.monthlyLimit}
          yearlyCostLimit={costLimitQuery.data?.yearlyLimit}
          isOverviewLoading={overviewQuery.isLoading}
          isOverviewError={overviewQuery.isError}
        />
      ),
    },
    {
      id: 'users',
      label: translate('lac.organisations.tabs.users'),
      icon: <Users aria-hidden />,
      content: (
        <LacTenantMembersTab
          tenantName={tenantName}
          tenantDisplayName={headingName}
          maxUsers={tenant.maxUsers}
        />
      ),
    },
    {
      id: 'stats',
      label: translate('lac.organisations.tabs.stats'),
      icon: <BarChart3 aria-hidden />,
      content: <LacTenantOverviewStats tenantName={tenantName} />,
    },
    {
      id: 'documents',
      label: translate('lac.organisations.tabs.documents'),
      icon: <FileText aria-hidden />,
      content: <LacDocumentsTab tenantName={tenantName} />,
    },
    {
      id: 'website-crawls',
      label: translate('lac.organisations.tabs.websiteCrawls'),
      icon: <Globe aria-hidden />,
      content: <LacWebsiteCrawlsTab tenantName={tenantName} />,
    },
  ];

  return (
    <LiksPage
      size="big"
      className="lac-tenant-details"
      header={
        <LiksPageHeading
          aria-label={translate('lac.organisations.details.breadcrumbLegend')}
          items={[
            {
              label: translate('lac.title'),
              to: resolveLacTabPath('organisations'),
            },
            { label: headingName },
          ]}
        />
      }
      content={
        <div className="lac-tenant-details__content">
          <LiksTabs
            tabs={tabs}
            activeTabId={activeTabId}
            onActiveTabIdChange={handleTabChange}
            aria-label={translate('lac.organisations.tabs.legend')}
          />
        </div>
      }
    />
  );
}
